/**
 * Medication Quantity and Frequency Calculation Utilities
 *
 * Implements safe, standardized auto-calculation of medication quantities:
 * 1. Correctly calculates daily, hourly, and weekly frequencies.
 *    - "Twice weekly" calculates as (2 / 7) of days, not twice daily.
 *    - "Weekly" calculates as (1 / 7) of days.
 * 2. Leaves quantity empty for ambiguous/as-needed frequencies (PRN, "every other day", unclear text)
 *    so the nurse types the required amount manually.
 * 3. Protects user-typed quantities from being silently overwritten when dates or frequencies change.
 */

/**
 * Determines the daily multiplier for a given frequency string.
 * Returns `null` if the frequency cannot be safely and clearly determined.
 */
export const getFrequencyMultiplier = (frequency?: string): number | null => {
  if (!frequency) return null;
  const s = frequency.toLowerCase().trim();
  if (!s) return null;

  // 1. Explicitly check for ambiguous / as-needed / non-daily frequencies
  // These must return null so the quantity field is left empty for the nurse.
  if (
    s.includes("prn") ||
    s.includes("as needed") ||
    s.includes("when required") ||
    s.includes("pro re nata") ||
    s.includes("other day") || // "every other day"
    s.includes("qod") ||
    s.includes("q.o.d") ||
    s.includes("alternate") ||
    s.includes("as directed") ||
    s.includes("stat") ||
    s === "other"
  ) {
    return null;
  }

  // 2. Check for Multi-week intervals FIRST (e.g. "every 2 weeks", "every 2 week", "biweekly", "every other week", "q2w", "every 3 weeks")
  if (
    s.includes("biweekly") ||
    s.includes("bi-weekly") ||
    s.includes("every other week")
  ) {
    return 1 / 14;
  }

  // Regex matching "every X weeks", "every X week", "every Xth week", "qXw", or "X weeks"
  const multiWeekMatch =
    s.match(/every\s+(\d+)\s*(?:st|nd|rd|th)?\s*(?:weeks?|wks?|wk)\b/) ||
    s.match(/\bq(\d+)w(?:ks?|k)?\b/) ||
    s.match(/^(\d+)\s*(?:weeks?|wks?|wk)$/);

  if (multiWeekMatch) {
    const weeks = parseInt(multiWeekMatch[1], 10);
    if (!isNaN(weeks) && weeks > 0) {
      return 1 / (weeks * 7);
    }
  }

  // Word-based numbers for multi-week intervals
  if (s.includes("every two week") || s.includes("every two wks")) {
    return 1 / 14;
  }
  if (s.includes("every three week") || s.includes("every three wks")) {
    return 1 / 21;
  }
  if (s.includes("every four week") || s.includes("every four wks")) {
    return 1 / 28;
  }

  // 3. Weekly frequencies (X times a week or weekly)
  const isWeekly = s.includes("week") || s.includes("weekly");
  if (isWeekly) {
    if (s.includes("4 time") || s.includes("four time") || s.includes("4x")) {
      return 4 / 7;
    }
    if (s.includes("3 time") || s.includes("three time") || s.includes("3x")) {
      return 3 / 7;
    }
    if (
      s.includes("twice") ||
      s.includes("2 time") ||
      s.includes("two time") ||
      s.includes("2x")
    ) {
      return 2 / 7;
    }
    if (
      s.includes("once") ||
      s.includes("1 time") ||
      s.includes("one time") ||
      s.includes("1x") ||
      s === "weekly" ||
      s.includes("once a week") ||
      s.includes("once weekly") ||
      s.includes("every week")
    ) {
      return 1 / 7;
    }

    const weekMatch = s.match(/(\d+)\s*(?:times?|x)\s*(?:a\s*week|weekly|per\s*week)?/);
    if (weekMatch) {
      const num = parseInt(weekMatch[1], 10);
      if (!isNaN(num) && num > 0) return num / 7;
    }
    return 1 / 7; // Default weekly
  }

  // 3. Hourly intervals (every X hours)
  if (s.includes("every 24 hours") || s.includes("q24h")) {
    return 1;
  }
  if (s.includes("every 12 hours") || s.includes("q12h")) {
    return 2;
  }
  if (s.includes("every 8 hours") || s.includes("q8h")) {
    return 3;
  }
  if (s.includes("every 6 hours") || s.includes("q6h")) {
    return 4;
  }
  if (s.includes("every 4 hours") || s.includes("q4h")) {
    return 6;
  }
  if (s.includes("every 3 hours") || s.includes("q3h")) {
    return 8;
  }
  if (s.includes("every 2 hours") || s.includes("q2h")) {
    return 12;
  }
  if (
    s.includes("every hour") ||
    s.includes("every 1 hour") ||
    s.includes("q1h") ||
    s.includes("qh")
  ) {
    return 24;
  }

  const everyHourMatch = s.match(/every\s+(\d+)\s*(?:hours?|hrs?)/);
  if (everyHourMatch) {
    const hours = parseInt(everyHourMatch[1], 10);
    if (!isNaN(hours) && hours > 0 && hours <= 24) {
      return 24 / hours;
    }
  }

  // 4. Daily frequencies
  // 4 times daily
  if (
    s.includes("four time") ||
    s.includes("4 time") ||
    s.includes("4x daily") ||
    s.includes("4 x daily") ||
    s.includes("qid") ||
    s.includes("q.i.d")
  ) {
    return 4;
  }

  // 3 times daily
  if (
    s.includes("three time") ||
    s.includes("3 time") ||
    s.includes("3x daily") ||
    s.includes("3 x daily") ||
    s.includes("tid") ||
    s.includes("t.i.d")
  ) {
    return 3;
  }

  // 2 times daily (twice a day)
  if (
    s.includes("twice") ||
    s.includes("two time") ||
    s.includes("2 time") ||
    s.includes("2x daily") ||
    s.includes("2 x daily") ||
    s.includes("bid") ||
    s.includes("b.i.d") ||
    ((s.includes("breakfast") || s.includes("morning")) && (s.includes("dinner") || s.includes("evening") || s.includes("night")))
  ) {
    return 2;
  }

  // 1 time daily (once a day, including single meal-time schedules)
  if (
    s.includes("once daily") ||
    s.includes("once a day") ||
    s.includes("one time") ||
    s.includes("1 time") ||
    s.includes("1x daily") ||
    s.includes("1 x daily") ||
    s === "once" ||
    s === "daily" ||
    s.includes("q.d") ||
    s.includes("qd") ||
    s.includes("qam") ||
    s.includes("qpm") ||
    s.includes("qhs") ||
    s === "hs" ||
    s.includes("morning") ||
    s.includes("bedtime") ||
    s.includes("evening") ||
    s.includes("night") ||
    s.includes("noon") ||
    s.includes("ac dinner") ||
    s.includes("pc dinner") ||
    s.includes("ac breakfast") ||
    s.includes("pc breakfast") ||
    s.includes("ac lunch") ||
    s.includes("pc lunch") ||
    s.includes("before dinner") ||
    s.includes("after dinner") ||
    s.includes("before breakfast") ||
    s.includes("after breakfast") ||
    s.includes("before lunch") ||
    s.includes("after lunch") ||
    s.includes("with breakfast") ||
    s.includes("with dinner") ||
    s.includes("with lunch") ||
    s.includes("breakfast") ||
    s.includes("dinner") ||
    s.includes("lunch") ||
    s.includes("supper")
  ) {
    return 1;
  }

  // Generic pattern for "X times a day" / "X times daily"
  const timesMatch = s.match(/(\d+)\s*(?:times?|x)\s*(?:a\s*day|daily|per\s*day)/);
  if (timesMatch) {
    const num = parseInt(timesMatch[1], 10);
    if (!isNaN(num) && num > 0) return num;
  }

  // 5. Unrecognized / unclear text -> return null (leave field empty so nurse types manually)
  return null;
};

/**
 * Calculates medication quantity based on start date, refill/end date, and frequency.
 * Returns a string representing the quantity, or "" if dates or frequency are unclear/invalid.
 */
export const calculateMedicationQuantity = (
  startDate: Date | string | null | undefined,
  endDate: Date | string | null | undefined,
  frequency?: string,
): string => {
  if (!startDate || !endDate || !frequency) return "";
  const start = new Date(startDate);
  start.setHours(0, 0, 0, 0);
  const end = new Date(endDate);
  end.setHours(0, 0, 0, 0);

  if (isNaN(start.getTime()) || isNaN(end.getTime()) || end < start) {
    return "";
  }

  const multiplier = getFrequencyMultiplier(frequency);
  if (multiplier === null || multiplier <= 0) {
    return "";
  }

  const diffTime = end.getTime() - start.getTime();
  // Inclusive calculation: count both start date and end date (e.g. 26/10 to 30/10 = 5 days)
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24)) + 1;
  const rawQty = Math.round(diffDays * multiplier);
  const qty = diffDays >= 1 && rawQty === 0 ? 1 : rawQty;
  return qty > 0 ? String(qty) : "";
};

/**
 * Determines whether the quantity field should be updated with a newly auto-calculated quantity.
 * Auto-fills ONLY if:
 * 1. The current quantity field is empty, OR
 * 2. The current quantity field still holds the last auto-filled value.
 *
 * If the nurse typed a custom value, this preserves the nurse's typed quantity.
 */
export const getNextQuantityOnFieldChange = (
  currentQuantity: string | undefined,
  lastAutoQuantity: string | undefined,
  newAutoQuantity: string,
): { quantity: string; lastAutoQuantity: string } => {
  const trimmedCurrent = (currentQuantity ?? "").trim();
  const trimmedLastAuto = (lastAutoQuantity ?? "").trim();

  // If the field is empty OR still holds the exact value that was auto-filled last time:
  const shouldAutoFill = trimmedCurrent === "" || trimmedCurrent === trimmedLastAuto;

  if (shouldAutoFill) {
    return {
      quantity: newAutoQuantity,
      lastAutoQuantity: newAutoQuantity,
    };
  }

  // Nurse manually typed a custom value: PRESERVE IT!
  return {
    quantity: currentQuantity ?? "",
    lastAutoQuantity: newAutoQuantity,
  };
};
